<template>
  <Modal
    ref="agreementModalRef"
    title="Sözleşmeyi Onaylayın"
    :show-icon="false"
    negative-action-text="Vazgeç"
    positive-action-text="Onayla"
    :is-positive-action-pending="isSubmitting"
    :is-positive-action-button-disabled="isSubmitting || !contractContent"
    :modal-style="{ width: '800px' }"
    :outside-scroll-disabled="true"
    class="wms-agreement-modal"
    @on-negative-action-click="handleCancel"
    @on-positive-action-click="handleAccept"
    @modal-closed="handleModalClosed"
  >
    <template #body>
      <div class="wms-agreement-modal-content">
        <div class="wms-agreement-info">
          <img
            src="@/assets/images/icons/info-circle.svg"
            alt=""
            width="20"
            height="20"
          />
          <p>
            n11 Depom'a erişmek için aşağıdaki güncel sözleşmeyi
            onaylamalısınız. Onayınız sonrası n11 Depom'u kullanabilir ve ilgili
            fırsatlardan yararlanabilirsiniz.
          </p>
        </div>

        <div class="wms-agreement-contract-wrapper">
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="contractContent"
            class="wms-agreement-contract-text"
            v-html="contractContent"
          />
          <!-- eslint-enable vue/no-v-html -->
          <div v-else class="wms-agreement-loading">
            <img src="@/assets/images/loading.gif" alt="Yükleniyor" />
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref } from 'vue';
import { Modal } from '@n11/so-ui-components';
import { acceptWmsAgreement } from '@/services/wmsAgreementService';

const props = defineProps({
  contractContent: {
    type: String,
    default: null,
  },
  redirectHref: {
    type: String,
    default: '/supplies-management',
  },
});

const emit = defineEmits(['cancel', 'accepted']);

const agreementModalRef = ref(null);
const isSubmitting = ref(false);
const isClosingByAction = ref(false);
const isHidingForCancel = ref(false);

const openModal = () => {
  isClosingByAction.value = false;
  agreementModalRef.value?.openModal();
};

const closeModal = () => {
  isClosingByAction.value = true;
  agreementModalRef.value?.hiddenModal();
};

const hideForCancel = () => {
  isHidingForCancel.value = true;
  agreementModalRef.value?.hiddenModal();
};

const handleCancel = () => {
  emit('cancel');
};

const handleModalClosed = () => {
  if (isHidingForCancel.value) {
    isHidingForCancel.value = false;
    return;
  }

  if (isClosingByAction.value) {
    isClosingByAction.value = false;
    return;
  }

  emit('cancel');
};

const handleAccept = async () => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    await acceptWmsAgreement();
    closeModal();
    emit('accepted', props.redirectHref);
  } catch (error) {
    console.error(error);
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ openModal, closeModal, hideForCancel });
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.wms-agreement-modal {
  :deep(.modal-wrapper .modal-container .modal-body .title) {
    margin: 0;
    display: flex;
    width: 100%;
  }

  :deep(.content) {
    text-align: left;
  }

  :deep(.modal-footer #negative-button),
  :deep(.modal-footer #positive-button) {
    height: 48px !important;
    font-size: 16px !important;
    padding: 0 24px !important;
    letter-spacing: -0.11px;
  }
}

.wms-agreement-modal-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.wms-agreement-info {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 6px;
  background-color: $colorB05;
  align-items: center;

  p {
    margin: 0;
    font-size: 13px;
    line-height: 18px;
    color: $colorN80;
    text-align: left;
    font-weight: 400;
    letter-spacing: -0.05px;
  }
}

.wms-agreement-contract-wrapper {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid $colorD30;
  border-radius: 6px;
  background-color: $colorN00;
  padding: 16px;
}

.wms-agreement-contract-text {
  :deep(h3) {
    font-size: 18px;
    font-weight: 600;
    color: $colorN80;
    margin: 24px 0 12px;
    text-align: left;
    &:first-child {
      margin-top: 0;
    }
  }

  :deep(p) {
    font-size: 14px;
    color: $colorN70;
    line-height: 20px;
    margin-bottom: 12px;
    text-align: left;
  }

  :deep(ul) {
    margin: 12px 0;
    padding-left: 20px;

    li {
      font-size: 14px;
      color: $colorN70;
      line-height: 20px;
      margin-bottom: 8px;

      strong {
        color: $colorN80;
      }
    }
  }
}

.wms-agreement-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;

  img {
    width: 48px;
    height: 48px;
  }
}
</style>
